import {SectionIntro, Section, Box, Grid, MediaPlaylist, Image} from '@primer/react-brand'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import type {DetailSectionProps} from './types'
import {renderWithoutParagraphs} from '../../../utils'
import {FlexSuiteRiverAccordion} from '../../../components/FlexSuiteRiverAccordion'
import {FlexSuiteRiverStack} from '../../../components/FlexSuiteRiverStack'
import {FlexSuiteRiverBreakout} from '../../../components/FlexSuiteRiverBreakout'
import {FlexSuiteRiverBreakoutTabs} from '../../../components/FlexSuiteRiverBreakoutTabs'
import {FlexSuiteRiverFootsteps} from '../../../components/FlexSuiteRiverFootsteps'
import {FlexSuiteLargeCards} from '../../../components/FlexSuiteLargeCards'
import {AssetTypeEnum} from '../../../types'
import {AssetVideo} from '../../../components/Video'

import {getImageSources} from '@github-ui/swp-core/lib/utils/images'

export function DetailSection({
  settings,
  sectionIntroHeading,
  sectionIntroLabel,
  sectionIntroDescription,
  sectionIntroLink,
  riverVariant,
  featureRiverAccordion,
  featureRiverStack,
  breakoutVariant,
  featureRiverBreakout,
  featureBreakoutTabs,
  cardsVariant,
  featureFootstepsLabel,
  featureFootstepsHeading,
  featureFootstepsLink,
  featureFootstepsCards,
  featureLargeCardsHeading,
  featureLargeCards,
  videoPlaylistHeading,
  videoPlaylistItems,
}: DetailSectionProps) {
  const brand = settings?.fields.brand
  return (
    <Section fullWidth paddingBlockStart="none" paddingBlockEnd="none">
      <Box paddingBlockEnd={{narrow: 'spacious', regular: 64}} paddingBlockStart={{narrow: 'spacious', regular: 64}}>
        <Grid>
          <Grid.Column>
            <SectionIntro align="center" fullWidth>
              {sectionIntroLabel && <SectionIntro.Label>{sectionIntroLabel}</SectionIntro.Label>}
              {sectionIntroHeading && (
                <SectionIntro.Heading size="3">{renderWithoutParagraphs(sectionIntroHeading)}</SectionIntro.Heading>
              )}
              {sectionIntroDescription && (
                <SectionIntro.Description>{renderWithoutParagraphs(sectionIntroDescription)}</SectionIntro.Description>
              )}
              {sectionIntroLink?.fields.href && (
                <SectionIntro.Link href={sectionIntroLink.fields.href}>
                  {sectionIntroLink.fields.text}
                </SectionIntro.Link>
              )}
            </SectionIntro>
          </Grid.Column>
        </Grid>
      </Box>

      {riverVariant === 'Accordion' && featureRiverAccordion ? (
        <Box paddingBlockEnd="spacious" paddingBlockStart="spacious">
          <Grid>
            <Grid.Column>
              <FlexSuiteRiverAccordion items={featureRiverAccordion} />
            </Grid.Column>
          </Grid>
        </Box>
      ) : null}

      {riverVariant === 'Stack' && featureRiverStack ? (
        <Section paddingBlockStart="none" paddingBlockEnd="none">
          <FlexSuiteRiverStack items={featureRiverStack} brand={brand} showDither />
        </Section>
      ) : null}

      {breakoutVariant === 'Breakout' && featureRiverBreakout ? (
        <Grid>
          <Grid.Column>
            <FlexSuiteRiverBreakout {...featureRiverBreakout.fields} />
          </Grid.Column>
        </Grid>
      ) : null}

      {breakoutVariant === 'Carousel' && featureBreakoutTabs ? (
        <Grid>
          <Grid.Column>
            <FlexSuiteRiverBreakoutTabs items={featureBreakoutTabs} />
          </Grid.Column>
        </Grid>
      ) : null}

      {cardsVariant === 'Footsteps' && featureFootstepsCards ? (
        <Box marginBlockStart={48} paddingBlockStart={48} paddingBlockEnd={48}>
          <FlexSuiteRiverFootsteps
            heading={featureFootstepsHeading}
            label={featureFootstepsLabel}
            cards={featureFootstepsCards}
            cta={featureFootstepsLink && featureFootstepsLink.fields}
          />
        </Box>
      ) : null}

      {cardsVariant === 'Large Cards' && featureLargeCards ? (
        <Box marginBlockStart={48} paddingBlockStart={48} paddingBlockEnd={48}>
          <FlexSuiteLargeCards heading={featureLargeCardsHeading} cards={featureLargeCards} />
        </Box>
      ) : null}

      {videoPlaylistItems && videoPlaylistItems.length > 0 ? (
        <Box paddingBlockStart="spacious" paddingBlockEnd="spacious">
          <Grid>
            <Grid.Column>
              <MediaPlaylist>
                {videoPlaylistHeading && (
                  <MediaPlaylist.Heading as="h3">{renderWithoutParagraphs(videoPlaylistHeading)}</MediaPlaylist.Heading>
                )}
                {videoPlaylistItems.map(item => {
                  const {
                    heading,
                    description,
                    assetType,
                    optionImage,
                    optionVideo,
                    optionPoster,
                    autoplay,
                    videoSource,
                  } = item.fields
                  const thumbnailUrl = optionPoster?.fields?.file?.url || optionImage?.fields?.file?.url

                  return (
                    <MediaPlaylist.Item
                      key={item.sys.id}
                      thumbnail={thumbnailUrl ? <Image src={thumbnailUrl} alt="" /> : undefined}
                    >
                      <MediaPlaylist.ItemHeading
                        description={description ? documentToPlainTextString(description) : undefined}
                      >
                        {documentToPlainTextString(heading)}
                      </MediaPlaylist.ItemHeading>
                      <MediaPlaylist.ItemContent />
                      <MediaPlaylist.ItemMedia>
                        {assetType === AssetTypeEnum.enum.Image && optionImage && (
                          <Image
                            as="picture"
                            src={`${optionImage.fields.file.url}?fm=webp`}
                            sources={getImageSources(optionImage.fields.file.url, {maxWidth: 720})}
                            alt={optionImage.fields.description ?? ''}
                          />
                        )}
                        <AssetVideo
                          assetType={assetType}
                          optionVideo={optionVideo}
                          optionPoster={optionPoster}
                          autoplay={autoplay}
                          videoSource={videoSource}
                        />
                      </MediaPlaylist.ItemMedia>
                    </MediaPlaylist.Item>
                  )
                })}
              </MediaPlaylist>
            </Grid.Column>
          </Grid>
        </Box>
      ) : null}
    </Section>
  )
}
